import { FormattedMessage, useIntl, type IntlShape } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import LeLiveHeading from './LeLiveHeading'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'

interface Partner {
  id: string
  logoId: string
  logoHeight: number
  mobileLogoHeight?: number
  format?: 'svg' | 'png'
}

function formatPartnerName(intl: IntlShape, partner: Partner): string {
  switch (partner.id) {
    case 'mastercard':
      return intl.formatMessage({
        id: 'ByWcwrODkw4W-UGuWp4B',
        defaultMessage: 'Mastercard',
        description: 'Partner name shown as alternative text for the Mastercard logo in the LE Live partners section',
      })
    case 'cathay':
      return intl.formatMessage({
        id: 'OGEf2LxVCAjHvOfRqqm5',
        defaultMessage: 'Cathay Pacific',
        description: 'Partner name shown as alternative text for the Cathay Pacific logo in the LE Live partners section',
      })
    case 'passportcard':
      return intl.formatMessage({
        id: 'XEqR1FgD3gIo_qJZvz5p',
        defaultMessage: 'PassportCard',
        description: 'Partner name shown as alternative text for the PassportCard logo in the LE Live partners section',
      })
    case 'hktb':
      return intl.formatMessage({
        id: 'hQeAXQtn-koVh-sq1pod',
        defaultMessage: 'Hong Kong Tourism Board',
        description: 'Partner name shown as alternative text for the Hong Kong Tourism Board logo in the LE Live partners section',
      })
    case 'lavazza':
      return intl.formatMessage({
        id: 'T1eaXRHXGftcrAJp7EAE',
        defaultMessage: 'Lavazza',
        description: 'Partner name shown as alternative text for the Lavazza logo in the LE Live partners section',
      })
    case 'wine-selectors':
      return intl.formatMessage({
        id: 'u05k4Kp9NgOiXr_aCes4',
        defaultMessage: 'Wine Selectors',
        description: 'Partner name shown as alternative text for the Wine Selectors logo in the LE Live partners section',
      })
    case 'ep-apurva':
      return intl.formatMessage({
        id: 'BKBf6TKngbNqgZa9aETz',
        defaultMessage: 'Apurva',
        description: 'Partner name shown as alternative text for the Apurva logo in the LE Live event partners section',
      })
    case 'ep-centara-anda-dhevi':
      return intl.formatMessage({
        id: 'FJwSUo-csrbLVDpctTV4',
        defaultMessage: 'Centara Anda Dhevi',
        description: 'Partner name shown as alternative text for the Centara Anda Dhevi logo in the LE Live event partners section',
      })
    case 'ep-australian-bespoke':
      return intl.formatMessage({
        id: 'RDyKOiTHOcrIJpDZ5EEc',
        defaultMessage: 'Australian Bespoke',
        description: 'Partner name shown as alternative text for the Australian Bespoke logo in the LE Live event partners section',
      })
    case 'ep-celebrity-cruises':
      return intl.formatMessage({
        id: 'sIFU-9XeyHQZ7iomB5_Y',
        defaultMessage: 'Celebrity Cruises',
        description: 'Partner name shown as alternative text for the Celebrity Cruises logo in the LE Live event partners section',
      })
    case 'ep-centara-beach-resort':
      return intl.formatMessage({
        id: '3b0CNHahGb37m3v3gfqY',
        defaultMessage: 'Centara Beach Resort',
        description: 'Partner name shown as alternative text for the Centara Beach Resort logo in the LE Live event partners section',
      })
    case 'ep-como':
      return intl.formatMessage({
        id: 'K7AXr-yg2kR5gNR2AEdo',
        defaultMessage: 'COMO Hotels and Resorts',
        description: 'Partner name shown as alternative text for the COMO Hotels and Resorts logo in the LE Live event partners section',
      })
    case 'ep-journey-beyond':
      return intl.formatMessage({
        id: '6Lj1TrpIedbHj85s-BlZ',
        defaultMessage: 'Journey Beyond',
        description: 'Partner name shown as alternative text for the Journey Beyond logo in the LE Live event partners section',
      })
    case 'ep-ayodya':
      return intl.formatMessage({
        id: '47z6FCgCVNFlu0bGEhQL',
        defaultMessage: 'Ayodya',
        description: 'Partner name shown as alternative text for the Ayodya logo in the LE Live event partners section',
      })
    case 'ep-centara-hotel-osaka':
      return intl.formatMessage({
        id: '8IWUj57lRvqeoSLmtciE',
        defaultMessage: 'Centara Hotel Osaka',
        description: 'Partner name shown as alternative text for the Centara Hotel Osaka logo in the LE Live event partners section',
      })
    case 'ep-katathani':
      return intl.formatMessage({
        id: 'quEy93hTTe56BFZBLjP0',
        defaultMessage: 'Katathani Collection',
        description: 'Partner name shown as alternative text for the Katathani Collection logo in the LE Live event partners section',
      })
    case 'ep-banyan-group':
      return intl.formatMessage({
        id: 'W8sxDX6VkqPu7IKpNA22',
        defaultMessage: 'Banyan Group',
        description: 'Partner name shown as alternative text for the Banyan Group logo in the LE Live event partners section',
      })
    case 'ep-centara-lagoon-maldives':
      return intl.formatMessage({
        id: '9mERlcj_bvfZPzmGt0wp',
        defaultMessage: 'Centara Lagoon Maldives',
        description: 'Partner name shown as alternative text for the Centara Lagoon Maldives logo in the LE Live event partners section',
      })
    case 'ep-ennismore':
      return intl.formatMessage({
        id: 'mxorA5bPrtlj3DyA_rgB',
        defaultMessage: 'Ennismore',
        description: 'Partner name shown as alternative text for the Ennismore logo in the LE Live event partners section',
      })
    case 'ep-kayumanis':
      return intl.formatMessage({
        id: 'mxnmYx-0q5st2n16ABXh',
        defaultMessage: 'Kayumanis',
        description: 'Partner name shown as alternative text for the Kayumanis logo in the LE Live event partners section',
      })
    case 'ep-double-six':
      return intl.formatMessage({
        id: 'uv1RVjCogOXBq3LDcEVF',
        defaultMessage: 'Double Six',
        description: 'Partner name shown as alternative text for the Double Six logo in the LE Live event partners section',
      })
    case 'ep-centara-karon-villas':
      return intl.formatMessage({
        id: 'HX9P2qQ7FHXN0Ooon_5r',
        defaultMessage: 'Centara Karon Villas',
        description: 'Partner name shown as alternative text for the Centara Karon Villas logo in the LE Live event partners section',
      })
    case 'ep-ini-vie-group':
      return intl.formatMessage({
        id: 'QBMo_EdvSx-BY6DUPM7j',
        defaultMessage: 'Ini Vie Group',
        description: 'Partner name shown as alternative text for the Ini Vie Group logo in the LE Live event partners section',
      })
    case 'ep-the-mulia':
      return intl.formatMessage({
        id: 'Nyo6K5MiZqnXmhUtchNK',
        defaultMessage: 'The Mulia',
        description: 'Partner name shown as alternative text for The Mulia logo in the LE Live event partners section',
      })
    case 'ep-cook-islands':
      return intl.formatMessage({
        id: '7Bj6Zf0o2J60UcSezXtT',
        defaultMessage: 'Cook Islands',
        description: 'Partner name shown as alternative text for the Cook Islands logo in the LE Live event partners section',
      })
    case 'ep-centara-mirage-lagoon-maldives':
      return intl.formatMessage({
        id: '5hG2vnpCj8O8D4cRkwFj',
        defaultMessage: 'Centara Mirage Lagoon Maldives',
        description: 'Partner name shown as alternative text for the Centara Mirage Lagoon Maldives logo in the LE Live event partners section',
      })
    case 'ep-miral':
      return intl.formatMessage({
        id: '56XD4r3a4ZVigvdZf7F4',
        defaultMessage: 'Miral',
        description: 'Partner name shown as alternative text for the Miral logo in the LE Live event partners section',
      })
    case 'ep-oceania-cruises':
      return intl.formatMessage({
        id: 'pDmX5c07wQXTw4hrVZU4',
        defaultMessage: 'Oceania Cruises',
        description: 'Partner name shown as alternative text for the Oceania Cruises logo in the LE Live event partners section',
      })
    case 'ep-princess-cruises':
      return intl.formatMessage({
        id: 'ue40Zjq0LpzedhJjvbsU',
        defaultMessage: 'Princess Cruises',
        description: 'Partner name shown as alternative text for the Princess Cruises logo in the LE Live event partners section',
      })
    case 'ep-centara-villas-phi-phi':
      return intl.formatMessage({
        id: 'w6WCmPeoydh9i0PckJ-R',
        defaultMessage: 'Centara Villas Phi Phi',
        description: 'Partner name shown as alternative text for the Centara Villas Phi Phi logo in the LE Live event partners section',
      })
    case 'ep-potique':
      return intl.formatMessage({
        id: 'EezzMkvHIwDUZjYMLHta',
        defaultMessage: 'Potique Hotels',
        description: 'Partner name shown as alternative text for the Potique Hotels logo in the LE Live event partners section',
      })
    case 'ep-insight-vacations':
      return intl.formatMessage({
        id: 'lKKs8mOxtET5g9A7a0-r',
        defaultMessage: 'Insight Vacations',
        description: 'Partner name shown as alternative text for the Insight Vacations logo in the LE Live event partners section',
      })
    case 'ep-scenic-emerald':
      return intl.formatMessage({
        id: 'aac-UQtvdmWiYIasmvPA',
        defaultMessage: 'Scenic - Emerald Cruises and Tours',
        description: 'Partner name shown as alternative text for the Scenic Emerald Cruises and Tours logo in the LE Live event partners section',
      })
    case 'ep-centara-villas-samui':
      return intl.formatMessage({
        id: 'Bnu4p7ah3OFtx9r7rSxK',
        defaultMessage: 'Centara Villas Samui',
        description: 'Partner name shown as alternative text for the Centara Villas Samui logo in the LE Live event partners section',
      })
    case 'ep-tourism-wa':
      return intl.formatMessage({
        id: 'dCX3dgLNbNQiWHbuRIOJ',
        defaultMessage: 'Tourism WA',
        description: 'Partner name shown as alternative text for the Tourism WA logo in the LE Live event partners section',
      })
    case 'ep-scenic':
      return intl.formatMessage({
        id: 'KYewDvLHFf9qpTBHv6Qk',
        defaultMessage: 'Scenic',
        description: 'Partner name shown as alternative text for the Scenic logo in the LE Live event partners section',
      })
    case 'ep-trafalgar':
      return intl.formatMessage({
        id: 'LC0hdjtZj6jc383wlPGQ',
        defaultMessage: 'Trafalgar',
        description: 'Partner name shown as alternative text for the Trafalgar logo in the LE Live event partners section',
      })
    case 'ep-uniworld':
      return intl.formatMessage({
        id: 'Ri5WO8ZM3vicu2CSqxwt',
        defaultMessage: 'Uniworld',
        description: 'Partner name shown as alternative text for the Uniworld logo in the LE Live event partners section',
      })
    case 'ep-ytl':
      return intl.formatMessage({
        id: 'zgkuYPsMlndEKUl315Nw',
        defaultMessage: 'YTL Hotels',
        description: 'Partner name shown as alternative text for the YTL Hotels logo in the LE Live event partners section',
      })
    case 'ep-garuda-indonesia':
      return intl.formatMessage({
        id: 'fBPzLNbKqnUFBBhJiNKr',
        defaultMessage: 'Garuda Indonesia',
        description: 'Partner name shown as alternative text for the Garuda Indonesia logo in the LE Live event partners section',
      })
    case 'ep-plus-many-more':
      return intl.formatMessage({
        id: 'un1PZfVwvt9uv13Fzl_i',
        defaultMessage: 'Plus many more',
        description: 'Placeholder text shown as alternative text for the plus many more logo in the LE Live event partners section',
      })
    default:
      return partner.id.replace(/^ep-/, '').replace(/-/g, ' ')
  }
}

const MAJOR_PARTNERS: Array<Partner> = [
  { id: 'mastercard', logoId: '9sz94m8h9agkc5dm9jp', logoHeight: 88 },
  { id: 'cathay', logoId: 'aq931u2xbi6gpmlfwu6', logoHeight: 32 },
  { id: 'passportcard', logoId: 'jnlxnrbgk10uiyhryeus', logoHeight: 88, mobileLogoHeight: 52 },
  { id: 'hktb', logoId: 'o54w6ee3jp1h7kgbsak', logoHeight: 88 },
  { id: 'lavazza', logoId: 'y29wimf6ge2vkc2edudb', logoHeight: 48 },
  { id: 'wine-selectors', logoId: 'zpltmmy3zq0vwwrturd', logoHeight: 36 },
]

const EVENT_PARTNERS: Array<Partner> = [
  { id: 'ep-apurva', logoId: 'tnwlt5opmdxd1ob60ev', logoHeight: 100, mobileLogoHeight: 60 },
  { id: 'ep-centara-anda-dhevi', logoId: 'ibqg1ejyecm3rjeej5fo', logoHeight: 80 },
  { id: 'ep-australian-bespoke', logoId: 'v0n11ga9dftfb90yumr', logoHeight: 60 },
  { id: 'ep-celebrity-cruises', logoId: 'hvqw9rif4asr9ikpk3yc', logoHeight: 36 },
  { id: 'ep-centara-beach-resort', logoId: '4epzjtq3754u918pkg', logoHeight: 84 },
  { id: 'ep-como', logoId: 'lcm9h7v6hblljgv1l8w4', logoHeight: 60 },
  { id: 'ep-journey-beyond', logoId: 'vmj406jera7a8lzpcjvs', logoHeight: 48 },
  { id: 'ep-ayodya', logoId: 'jt4xhl125g6qptyhjnzu', logoHeight: 72, format: 'svg' },
  { id: 'ep-centara-hotel-osaka', logoId: 'uvyo4mtyeis1tiu4f3ch', logoHeight: 84 },
  { id: 'ep-katathani', logoId: '2yiuod0dju6grlvwk84v', logoHeight: 84, mobileLogoHeight: 60 },
  { id: 'ep-banyan-group', logoId: 'r2sq5xseprcdxpd0lkh', logoHeight: 36 },
  { id: 'ep-centara-lagoon-maldives', logoId: 'ru2jejxq5r2zedoqe2in', logoHeight: 84 },
  { id: 'ep-ennismore', logoId: 'trjrz60bjhfofj4hf9m', logoHeight: 48 },
  { id: 'ep-kayumanis', logoId: 'kh47wcchlmi98nvlwsz', logoHeight: 72 },
  { id: 'ep-double-six', logoId: 'qkvu353wr41vv7osdiwp', logoHeight: 72 },
  { id: 'ep-centara-karon-villas', logoId: 'v5fnhli2qifxlu9t1cb', logoHeight: 36 },
  { id: 'ep-ini-vie-group', logoId: 't3ek3v0to9rxzx4620n', logoHeight: 84, mobileLogoHeight: 60 },
  { id: 'ep-the-mulia', logoId: '26mki3yhzqndhv53uoec', logoHeight: 72 },
  { id: 'ep-cook-islands', logoId: 'j8cc9c14ss9r973e02', logoHeight: 36 },
  { id: 'ep-centara-mirage-lagoon-maldives', logoId: 'u6n93s3jqg04jcs4763h', logoHeight: 60 },
  { id: 'ep-miral', logoId: 'u6bvot5oy8tlbzmnbhl', logoHeight: 48 },
  { id: 'ep-oceania-cruises', logoId: '53kwmp9hw6x84lbyt9k9', logoHeight: 48 },
  { id: 'ep-princess-cruises', logoId: 'n1us3p4y4kxgjw5z04ln', logoHeight: 48 },
  { id: 'ep-centara-villas-phi-phi', logoId: 'aji3bcxg5cvkqlz2ca3d', logoHeight: 80 },
  { id: 'ep-potique', logoId: 'x096lzv46a9vfaob5zus', logoHeight: 82 },
  { id: 'ep-insight-vacations', logoId: 'qnf7jh6y2k1083d54hyh', logoHeight: 48 },
  { id: 'ep-scenic-emerald', logoId: 'hbrp2aifgpher5o0ylug', logoHeight: 144 },
  { id: 'ep-centara-villas-samui', logoId: '24mwzgyyi9tkmgakz6bej', logoHeight: 80 },
  { id: 'ep-tourism-wa', logoId: '7y2vonde5oa5b9zo8pan', logoHeight: 48, mobileLogoHeight: 32 },
  { id: 'ep-scenic', logoId: '1h4h2hbxgk5tle66v989', logoHeight: 48 },
  { id: 'ep-trafalgar', logoId: 'u293fbl53w5ch38meb6a', logoHeight: 38 },
  { id: 'ep-uniworld', logoId: 'j9fmishkzju3sy3j8w', logoHeight: 72 },
  { id: 'ep-ytl', logoId: 'nsy0lsex48v404d4yrr', logoHeight: 120 },
  { id: 'ep-garuda-indonesia', logoId: 'st24joxq10gsn1iggzc', logoHeight: 96 },
  { id: 'ep-plus-many-more', logoId: 'lkcx8mqvbwwosw6spnqb', logoHeight: 60 },
]

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};
  width: 100%;

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const LogoCard = styled(Sheet)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(73)};
  min-width: ${rem(100)};
  overflow: hidden;

  picture {
    display: flex;
    justify-content: center;
    max-width: none;
  }

  img {
    max-width: 100%;
    object-fit: contain;
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(138)};
  }
`

const LogoImageWrapper = styled.div<{ $mobileMaxHeight?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.$mobileMaxHeight && `
    ${mediaQueryDown.tablet} {
      img {
        max-height: ${rem(props.$mobileMaxHeight)};
        width: auto;
      }
    }
  `}
`

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(var(--marquee-scroll-offset)); }
`

const MarqueeTrack = styled.div`
  overflow: hidden;
  width: 100%;
`

const SECONDS_PER_ITEM = 2

const MarqueeInner = styled.div<{ $itemCount: number }>`
  display: flex;
  gap: ${rem(12)};
  width: max-content;
  animation: ${marqueeScroll} ${props => props.$itemCount * SECONDS_PER_ITEM}s linear infinite;
  --marquee-scroll-offset: -50%;

  &:dir(rtl) {
    --marquee-scroll-offset: 50%;
  }

  &:hover {
    animation-play-state: paused;
  }

  ${LogoCard} {
    width: ${rem(150)};
    flex-shrink: 0;

    ${mediaQueryUp.desktop} {
      width: ${rem(200)};
    }
  }
`

interface Props {
  hasRegistration: boolean
}

function LeLivePartners({ hasRegistration }: Props) {
  const intl = useIntl()

  return (
    <Sheet padding="56 20" desktopPadding="64 0" id="partners">
      <LayoutContainer>
        <Group direction="vertical" gap={40}>
          <Group direction="vertical" gap={8}>
            <LeLiveHeading variant="heading1alt" as="h2" align="center">
              <FormattedMessage
                id="qfScGL2DySI0NR7jHjJs"
                defaultMessage="Meet our <em>partners</em>"
                description="Main heading for the partners section on the LE Live event page, with emphasis styling on the word partners"
                values={{
                  em: (chunks) => <em>{chunks}</em>,
                }}
              />
            </LeLiveHeading>
            <BodyText variant="large" colour="light-foreground-alt" align="center">
              {intl.formatMessage({
                id: 'VHGUOGKbDmF9TTekuwTR',
                defaultMessage: 'Explore offerings from the world’s most trusted brands, all under one roof.',
                description: 'Partners section subtitle describing partner offerings',
              })}
            </BodyText>
          </Group>

          <Group direction="vertical" gap={40}>
            <Group direction="vertical" gap={12}>
              <BodyText variant="large" colour="light-foreground-alt" align="center">
                {intl.formatMessage({
                  id: 'UaZzYkwAe2ut-g3sJIlh',
                  defaultMessage: 'Major Partners',
                  description: 'Section label above the major partners logo grid on the LE Live event landing page',
                })}
              </BodyText>
              <LogoGrid>
                {MAJOR_PARTNERS.map(partner => (
                  <LogoCard key={partner.id} border="light-foreground" borderRadius="M" padding="16 12">
                    <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                      <Image
                        id={partner.logoId}
                        alt={formatPartnerName(intl, partner)}
                        height={partner.logoHeight}
                        dpr={2}
                        enhancedFormats={false}
                      />
                    </LogoImageWrapper>
                  </LogoCard>
                ))}
              </LogoGrid>
            </Group>

            <Group direction="vertical" gap={12}>
              <BodyText variant="large" colour="light-foreground-alt" align="center">
                {intl.formatMessage({
                  id: 'vtyBiC1JL9ygB7PKvsC9',
                  defaultMessage: 'Event Partners',
                  description: 'Section label above the scrolling event partners marquee on the LE Live event landing page',
                })}
              </BodyText>
              <MarqueeTrack>
                <MarqueeInner $itemCount={EVENT_PARTNERS.length}>
                  {EVENT_PARTNERS.map(partner => (
                    <LogoCard key={partner.id} border="light-foreground" borderRadius="M" padding="16 12">
                      <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                        <Image
                          id={partner.logoId}
                          alt={formatPartnerName(intl, partner)}
                          height={partner.logoHeight}
                          format={partner.format}
                          dpr={partner.format === 'svg' ? undefined : 2}
                          enhancedFormats={partner.format !== 'svg' ? false : undefined}
                        />
                      </LogoImageWrapper>
                    </LogoCard>
                  ))}
                  {EVENT_PARTNERS.map(partner => (
                    <LogoCard key={`${partner.id}-dup`} border="light-foreground" borderRadius="M" padding="16 12" aria-hidden>
                      <LogoImageWrapper $mobileMaxHeight={partner.mobileLogoHeight}>
                        <Image
                          id={partner.logoId}
                          alt={formatPartnerName(intl, partner)}
                          height={partner.logoHeight}
                          format={partner.format}
                          dpr={partner.format === 'svg' ? undefined : 2}
                          enhancedFormats={partner.format !== 'svg' ? false : undefined}
                        />
                      </LogoImageWrapper>
                    </LogoCard>
                  ))}
                </MarqueeInner>
              </MarqueeTrack>
            </Group>
          </Group>

          <Group direction="horizontal" horizontalAlign="center">
            <LeLiveGetTicketsButton hidden={hasRegistration} />
          </Group>
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LeLivePartners
